import type { SourceDocument } from "../../api/content/sourcesApi";
import type { AnnotationSet } from "../../api/threads/liveMessages";
import { formatDocumentAddress, parseContentAddress } from "../../shared/references/contentReferences";
import type { EditableAnnotationEntity } from "./AnnotationTextEditor";
import type { EvidenceMark } from "./textEvidence";
import { hasValidAnnotationSpan, type SpannedAnnotationEntity } from "./annotationSpans";

/**
 * Where the annotations of a set (W-6) lie in the text of their document version. The
 * server anchors them in code points of the whole document (`span_anchoring.py`); the span
 * view works on one segment's text in UTF-16 units, as v1's did. This is the one place
 * that converts between the two.
 */

type Annotation = AnnotationSet["annotations"][number];

/** An annotation and its position in the set, which names it when it is edited. */
export interface IndexedAnnotation {
  readonly index: number;
  readonly annotation: Annotation;
}

export interface AnnotatedSegment {
  /** Empty for a document without segments: its whole text is one segment. */
  readonly segmentPath: string;
  readonly text: string;
  /** Where the segment starts in the document, in code points. */
  readonly start: number;
  /** Each with its index in the set as `id`, so an edit can name the annotation it changes. */
  readonly annotations: Array<SpannedAnnotationEntity<EditableAnnotationEntity>>;
}

export interface AnnotationPlacement {
  readonly segments: readonly AnnotatedSegment[];
  /** Annotations whose span is not in one segment, or whose quote is not the text there. */
  readonly unplaced: readonly IndexedAnnotation[];
}

/**
 * The document versions of the set, the set's document first (W-15): those it anchors
 * (`documents`) and those its annotations lie in, each with its annotations.
 */
export function groupByDocument(set: AnnotationSet): ReadonlyMap<string, readonly IndexedAnnotation[]> {
  const groups = new Map<string, IndexedAnnotation[]>([set.document, ...(set.documents ?? [])].map((document) => [document, []]));
  set.annotations.forEach((annotation, index) => {
    const address = parseContentAddress(annotation.segment);
    const document = address?.document.version == null ? set.document : formatDocumentAddress(address.document);
    const group = groups.get(document) ?? [];
    group.push({ index, annotation });
    groups.set(document, group);
  });
  return groups;
}

/** Each segment of the document with the annotations inside it, in text order. */
export function placeAnnotations(
  document: Pick<SourceDocument, "fullText" | "segments">,
  annotations: readonly IndexedAnnotation[],
): AnnotationPlacement {
  const units = document.segments.length > 0
    ? document.segments
    : [{ segmentPath: "", text: document.fullText, start: 0, end: Array.from(document.fullText).length }];
  const placed = units.map((unit) => ({ segmentPath: unit.segmentPath, text: unit.text, start: unit.start, annotations: [] as Array<SpannedAnnotationEntity<EditableAnnotationEntity>> }));
  // Built once per segment that holds an annotation, not once per annotation.
  const offsetTables = new Map<number, readonly number[]>();
  const offsetsOf = (position: number, text: string) => {
    const table = offsetTables.get(position) ?? utf16Offsets(text);
    offsetTables.set(position, table);
    return table;
  };
  const unplaced: IndexedAnnotation[] = [];
  for (const indexed of annotations) {
    const { annotation } = indexed;
    const position = units.findIndex((unit) => unit.start <= annotation.start && annotation.end <= unit.end);
    const unit = units[position];
    const segment = placed[position];
    if (unit === undefined || segment === undefined) {
      unplaced.push(indexed);
      continue;
    }
    const offsets = offsetsOf(position, unit.text);
    const entity: EditableAnnotationEntity = {
      id: String(indexed.index),
      type: annotation.label,
      value: annotation.quote,
      start: offsets[annotation.start - unit.start],
      end: offsets[annotation.end - unit.start],
      note: annotation.note,
    };
    if (hasValidAnnotationSpan(unit.text, entity)) {
      segment.annotations.push(entity);
    } else {
      unplaced.push(indexed);
    }
  }
  return { segments: placed, unplaced };
}

/**
 * Evidence anchored like annotations, in code points of the whole document, as each segment
 * draws it: by segment path, in that segment's UTF-16 offsets, clipped to the segment, so
 * evidence that runs over two parts shows in both (O-1).
 */
export function placeEvidence(
  document: Pick<SourceDocument, "fullText" | "segments">,
  marks: readonly EvidenceMark[],
): ReadonlyMap<string, readonly EvidenceMark[]> {
  const units = document.segments.length > 0
    ? document.segments
    : [{ segmentPath: "", text: document.fullText, start: 0, end: Array.from(document.fullText).length }];
  const placed = new Map<string, EvidenceMark[]>();
  for (const unit of units) {
    const inside = marks.filter((mark) => mark.start < unit.end && mark.end > unit.start);
    if (inside.length === 0) continue;
    const offsets = utf16Offsets(unit.text);
    // Its label goes with each piece, so words that run over two parts carry their bar's label in both.
    placed.set(unit.segmentPath, inside.map((mark) => ({
      key: mark.key,
      start: offsets[Math.max(mark.start, unit.start) - unit.start] ?? 0,
      end: offsets[Math.min(mark.end, unit.end) - unit.start] ?? unit.text.length,
      ...(mark.label === undefined ? {} : { label: mark.label }),
      ...(mark.tone === undefined ? {} : { tone: mark.tone }),
    })));
  }
  return placed;
}

/**
 * A span the view selected in a segment's text (UTF-16) as the server anchors it: code
 * points of the whole document. The way back from `placeAnnotations`, for editing.
 */
export function documentOffsetsOf(
  segment: Pick<AnnotatedSegment, "text" | "start">,
  span: { readonly start: number; readonly end: number },
): { start: number; end: number } {
  return {
    start: segment.start + Array.from(segment.text.slice(0, span.start)).length,
    end: segment.start + Array.from(segment.text.slice(0, span.end)).length,
  };
}

/** The UTF-16 offset of each code point of `text`, and of its end. */
function utf16Offsets(text: string): number[] {
  const offsets = [0];
  for (const codePoint of text) {
    offsets.push((offsets.at(-1) ?? 0) + codePoint.length);
  }
  return offsets;
}
